import { inject, Injectable } from '@angular/core';
import { createEffect } from '@ngrx/effects';
import { catchError, EMPTY, map, retry } from 'rxjs';
import { PCP_LOGGER, PcpLoggerInterface } from '@pcp/logging';
import { ClassroomFacade } from '../../public/yjs/classroom.facade';
import { yjsStateActions } from '../store/yjs-state.actions';

/**
 * Mirror effect: Y.Doc Y.Map → NgRx.
 *
 * Subscribes to the facade's hydration-gated `changes$` stream and dispatches
 * `lessonStateMirrored` so the reducer projects Y.Doc state into NgRx selectors.
 *
 * The facade's observable already prepends the current snapshot on subscribe,
 * so late-joiners receive the post-sync state without extra plumbing here.
 *
 * Error handling: `retry` recovers from transient errors (with budget reset on
 * success so a long-running effect can absorb occasional hiccups). On budget
 * exhaustion, the terminal `catchError` logs once with a specific message and
 * returns EMPTY — the effect dies loudly instead of looping silently.
 */
@Injectable()
export class LessonYjsMirrorEffects {
    private readonly facade = inject(ClassroomFacade);
    private readonly log: PcpLoggerInterface = inject(PCP_LOGGER).createScoped('yjs');

    readonly mirrorLessonState$ = createEffect(() =>
        this.facade.changes$.pipe(
            retry({ count: 5, delay: 1000, resetOnSuccess: true }),
            catchError((err) => {
                this.log.error('mirrorLessonState$ permanently failed — Y.Doc → NgRx sync stopped', err as Error);
                return EMPTY;
            }),
            map(changes => yjsStateActions.lessonStateMirrored({ changes })),
        ),
    );
}
